//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { LocalizationHelper, TypeHelper, ConfigHelper, TemplateHelper } from '../../../helpers/'
import { GridOptions, GridController, GridTypes } from '../grid/grid.controller'
import { COMPONENT_NAME } from './select-popover.component'


// Public Types

export interface IIMRSelectPopover<TObject> {
    selectedObject: TObject

    togglePopover(): void
}

export type IMRSelectPopoverOptions<TObject> = {
    gridOptions?: GridOptions
    multiSelect?: boolean
    target?: string | JQuery
    width?: number

    onInitialized?(imrSelectPopover: IIMRSelectPopover<TObject>): void
    onObjectSelected?(opObject: TObject): void
}

// Public Types End


export class IMRSelectPopoverController<TObject> implements ng.IComponentController, IIMRSelectPopover<TObject> {

    // Consts

    private static get GRID_NAME(): string { return `${COMPONENT_NAME}_grid` }
    private static get TREE_NAME(): string { return `${COMPONENT_NAME}_tree` }
    private static get DEFAULT_WIDTH(): number { return 800 }

    // Consts End


    // Members

    private options: IMRSelectPopoverOptions<any>

    private popover: DevExpress.ui.dxPopover
    private popoverOptions: DevExpress.ui.dxPopoverOptions
    private popoverTarget: string | JQuery

    private textBoxOptions: DevExpress.ui.dxTextBoxOptions
    private textBox: DevExpress.ui.dxTextBox

    private bottomToolbarOptions: DevExpress.ui.dxToolbarOptions

    private gridController: GridController<any>
    private gridOptions: GridOptions

    private treeController: any
    private treeOptions: any

    private isMultiSelect: boolean
    private isGridVisible: boolean
    private isTreeVisible: boolean
    private isTargetMode: boolean
    private isTextBoxNeeded: boolean
    private width: number;
    private selectButtonOptions: DevExpress.ui.dxButtonOptions
    private selectButton: DevExpress.ui.dxButton
    // Members End


    // Properties

    public get selectedObject(): TObject { return this.getSelectedObject() }

    // Properties End


    // Ctor

    constructor() { }

    // Ctor End


    // IIMRSelectPopoverComponent Implementation

    public togglePopover(): void {
        if (this.popover != null) {
            this.popover.toggle(!this.popover.option('visible'))
        }
    }

    // IIMRSelectPopoverComponent Implementation End


    // IComponentController Implementation 

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // IComponentController Implementation End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onObjectSelected(object: any): void {
        if (this.options != null && _.isFunction(this.options.onObjectSelected)) {
            this.options.onObjectSelected(object)
        }
    }

    // Events End


    // Component

    private initialize(): void {
        this.setDefaults()

        if (this.options != null) {
            this.setOptions()

            if (this.isGridVisible) {
                this.overwriteGridOptions()
            }
            else if (this.isTreeVisible) {
                this.overwriteTreeOptions()
            }

            const toInitialize: JQueryPromise<any>[] = [this.initializePopover(), this.initializedBottomToolbar()]

            $.when(...toInitialize)
                .then(() => {
                    if (!this.isTargetMode) {
                        return this.initializeTextBox()
                    }
                })
                .then(() => {
                    this.onInitialized()
                })
        }
    }

    private getSelectedObject(): TObject {
        if (this.gridController != null) {
            return this.gridController.selectedItem
        }

        return null
    }

    private selectObject(): void {
        this.onObjectSelected(this.getSelectedObject())
        this.togglePopover()
    }

    // Component End


    // Popover

    private calculatePopupWidth(): string | number {
        const windowWidth: number = $(window).innerWidth()

        return windowWidth > this.width ? this.width : windowWidth * 0.95
    }

    private initializePopover(): JQueryPromise<any> {
        this.popoverOptions = {
            height: 600,
            target: this.popoverTarget,
            width: () => this.calculatePopupWidth(),

            onInitialized: (e: any) => this.popover = e.component
        }

        return TypeHelper.onInitializedAll(this.popoverOptions)
    }

    // Popover End


    // Grid

    private onGridInitialized(gridController: GridController<any>): void {
        this.gridController = gridController
    }

    private onGridRowDoubleClick(e: any): void {
        this.selectObject()
        this.updateTextBoxValue()
    }

    private updateTextBoxValue(): void {
        if (this.textBox != null) {
            console.log(this.gridController.selectedItem.toString())
            this.textBox.option('value', this.gridController.selectedItem ? this.gridController.selectedItem.toString() : '')
            this.textBox.option('isValid', !!this.gridController.selectedItem)
        }
    }

    private overwriteGridOptions(): void {
        if (this.gridOptions != null) {
            const onInitializedOrg: Function = this.gridOptions.onInitialized

            this.gridOptions.onInitialized = (...args: any[]) => {
                if (_.isFunction(onInitializedOrg)) {
                    onInitializedOrg(...args)
                }

                this.onGridInitialized(args[1])
            }

            const onRowDoubleClickOrg: Function = this.gridOptions.onRowDoubleClick

            this.gridOptions.onRowDoubleClick = (...args: any[]) => {
                if (_.isFunction(onRowDoubleClickOrg)) {
                    onRowDoubleClickOrg(...args)
                }

                this.onGridRowDoubleClick(args[1])
            }

            if (this.gridOptions.name == null) {
                this.gridOptions.name = IMRSelectPopoverController.GRID_NAME
            }

            if (this.gridOptions.type == null) {
                this.gridOptions.type = GridTypes.PAGINATED_SMALL
            }

            if (this.gridOptions.buttonsOptions == null) {
                this.gridOptions.buttonsOptions = { enabled: true }
            }
        }
    }

    // Grid End


    // Tree

    private overwriteTreeOptions(): void {

    }

    // Tree End


    // Text Box 

    private initializeTextBox(): JQueryPromise<any> {
        this.setTextBoxOptions()
        this.setSelectButtonOptions()

        return TypeHelper.onInitializedAll(this.textBoxOptions)
    }

    private setTextBoxOptions(): void {
        this.textBoxOptions = {
            value: '',
            showClearButton: true,
            disabled: true,
            isValid: false,
            onInitialized: (e: any) => {
                this.textBox = e.component
                this.popover.option('target', this.textBox.element())
            },
            onFocusIn: (e: any) => {
                e.events.stopPropagation()
                this.togglePopover()
            },
        }
    }

    private setSelectButtonOptions(): void {
        this.selectButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.HEADLINE),
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            onInitialized: (e: any) => {
                this.selectButton = e.component
            },
            onClick: (e: any) => {
                this.togglePopover()
            }
        }
    }

    // Text Box End


    // Bottom Toolbar

    private initializedBottomToolbar(): any {
        this.bottomToolbarOptions = {
            items: [{
                location: 'after',
                widget: 'dxButton',
                options: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),

                    onClick: () => {
                        this.selectObject()
                        this.updateTextBoxValue()
                    }
                }
            }, {
                location: 'after',
                widget: 'dxButton',
                options: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),

                    onClick: () => {
                        this.togglePopover()
                    }
                }
            }]
        }
    }

    // Bottom Toolbar End


    // Options

    private setDefaults(): void {
        this.isMultiSelect = false
        this.isGridVisible = false
        this.isTreeVisible = false
        this.isTargetMode = false
        this.popoverTarget = null
        this.width = IMRSelectPopoverController.DEFAULT_WIDTH
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.target != null) {
                this.isTargetMode = true
                this.popoverTarget = this.options.target
            }
        }

        if (this.options.gridOptions != null) {
            this.isGridVisible = true
            this.gridOptions = _.clone(this.options.gridOptions)
        }

        if (this.options.width != null) {
            this.width = this.options.width
        }

        // if (this.options.treeOptions != null) {
        //     this.isTreeVisible = true
        //     this.treeOptions = _.clone(this.options.treeOptions)
        // }

        if (this.options.multiSelect != null) {
            this.isMultiSelect = this.options.multiSelect
        }
    }

    // Options End
}


